import React, { useState, useEffect, useRef } from "react";
// import PropTypes from 'prop-types';
import {
  Box,
  Button,
  CheckBox,
  Heading,
  Text,
  Tip,
  Accordion,
  AccordionPanel,
  Header,
  Image,
} from "grommet";
import { Trash, Edit, Add, CircleInformation, Link } from "grommet-icons";
import { presentToastSuccess, presentToastErrorContent } from "@shared/Toast";
import { presentLoading, dismissLoading } from "@shared/Loading";
import { useFetchHotel } from "./redux/hooks";
import { ProductsMenu, AddCategory } from "./";
import { CategoryService, PhotoService, ProductService } from "../../services";
import HotelService from "../../services/hotel.service";
import { presentAlertPrompt } from "@shared/Alert";
import { roundPrice } from "@common/utils";
import { AddProduct } from "./";
import { SortableList } from "@shared";
import noPhoto from "../../images/nophoto.jpg";
import "./SettingsUpsell.scss";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

export default function SettingsUpsell(props) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { hotel, fetchHotel } = useFetchHotel();
  const [categories, setCategories] = useState([]);
  const [openCategoryIds, setOpenCategoryIds] = useState([]);
  const categoryRefs = useRef({});
  const [openEdit, setOpenEdit] = useState(false);
  const [categoryId, setCategoryId] = useState(0);
  const [openEditProduct, setOpenEditProduct] = useState(false);
  const [productId, setProductId] = useState(0);

  const onRefresh = () => {
    presentLoading();
    CategoryService.getUpsells().then(
      (response) => {
        dismissLoading();
        setCategories(response.data);
      },
      (error) => {
        dismissLoading();
        presentToastErrorContent(error);
      },
    );
  };

  useEffect(() => {
    onRefresh();
  }, []);

  useEffect(() => {
    const connectionId = searchParams.get("connection");
    if (!connectionId || categories.length === 0) return;

    const category = categories.find(
      (item) =>
        item.isCategory &&
        String(item.commerceConnection?._id || item.commerceConnection) ===
          connectionId,
    );
    if (!category) return;

    const targetId = String(category._id);
    setOpenCategoryIds((current) =>
      current.includes(targetId) ? current : [...current, targetId],
    );
    requestAnimationFrame(() => {
      categoryRefs.current[targetId]?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  }, [categories, searchParams]);

  const handleCategoryActive = (categoryId, indexes) => {
    const targetId = String(categoryId);
    setOpenCategoryIds((current) =>
      indexes.length > 0
        ? [...new Set([...current, targetId])]
        : current.filter((id) => id !== targetId),
    );
  };

  const handleEditModalClose = (refresh) => {
    if (refresh) onRefresh();

    setOpenEdit(false);
  };

  const handleRemoveCategory = (categoryId) => {
    presentAlertPrompt({
      title: "Are you sure?",
      message:
        "Do you really want to delete this category? This will remove all the products included in this category.",
      onOK: () => {
        removeCategory(categoryId);
      },
      buttonOKText: "Delete",
      buttonOKColor: "status-critical",
    });
  };

  const removeCategory = (categoryId) => {
    CategoryService.deleteCategory(categoryId).then(
      (response) => {
        onRefresh();
        presentToastSuccess("Category successfully removed.");
      },
      (error) => {
        presentToastErrorContent(error);
      },
    );
  };

  const handleEdit = (categoryId) => {
    setCategoryId(categoryId);
    setOpenEdit(true);
  };

  const handleAdd = () => {
    setCategoryId(0);
    setOpenEdit(true);
  };

  ////Product

  const handleEditProductModalClose = (refresh) => {
    if (refresh) onRefresh();

    setOpenEditProduct(false);
  };

  const handleRemoveProduct = (productId) => {
    presentAlertPrompt({
      title: "Are you sure?",
      message: "Do you really want to delete this product?",
      onOK: () => {
        removeProduct(productId);
      },
      buttonOKText: "Delete",
      buttonOKColor: "status-critical",
    });
  };

  const removeProduct = (productId) => {
    ProductService.deleteProduct(productId).then(
      (response) => {
        onRefresh();
        presentToastSuccess("Product successfully removed.");
      },
      (error) => {
        presentToastErrorContent(error);
      },
    );
  };

  const handleEditProduct = (productId) => {
    setProductId(productId);
    setOpenEditProduct(true);
  };

  const handleAddProduct = () => {
    setProductId(0);
    setOpenEditProduct(true);
  };

  const providerName = (item) => {
    const provider =
      item.commerceConnection?.provider || item.commerceProvider || "";
    return provider ? provider.charAt(0).toUpperCase() + provider.slice(1) : "";
  };

  const CategoryDragHandle = ({ item }) => (
    <Box direction="row" gap="small" width="xlarge" align="center">
      <SortableList.DragHandle />
      <Box gap="xxsmall">
        <Text weight="bold">{item.name}</Text>
        {item.commerceConnection ? (
          <Text size="xsmall" color="dark-5">
            {providerName(item)} · {t("settingsUpsell.partnerManaged")} ·{" "}
            {t("settingsUpsell.productCount", {
              count: item.productCount || 0,
            })}
          </Text>
        ) : (
          <Text size="xsmall" color="dark-5">
            {t("settingsUpsell.manualCategory")}
          </Text>
        )}
      </Box>
    </Box>
  );

  const SortableItem = ({ item }) => {
    const partnerManaged = Boolean(item.commerceConnection);
    const itemId = String(item._id);

    return (
      <SortableList.Item id={item._id}>
        {item ? (
          item.isCategory ? (
            <Box
              className="upsell-category-accordion"
              fill="horizontal"
              ref={(element) => {
                categoryRefs.current[itemId] = element;
              }}
            >
              <Accordion
                className="accordion"
                activeIndex={openCategoryIds.includes(itemId) ? [0] : []}
                onActive={(indexes) => handleCategoryActive(item._id, indexes)}
              >
                <AccordionPanel
                  label={<CategoryDragHandle item={item} />}
                  background={
                    !item.name || item.active ? "#effbff" : "inactive"
                  }
                  style={{ height: 80, paddingLeft: 10 }}
                >
                  <Box
                    direction="row"
                    gap="medium"
                    justify="between"
                    margin="small"
                    round
                    background={
                      !item.name || item.active ? "light-2" : "inactive"
                    }
                    pad="small"
                    border
                  >
                    <Box
                      direction="row"
                      gap="medium"
                      width="400px"
                      align="start"
                    >
                      <Box height="70px" width="70px">
                        <Image
                          height="70px"
                          width="70px"
                          fit="cover"
                          src={PhotoService.getURL(item.photo) || noPhoto}
                          className="sortable-item-image"
                          onClick={
                            partnerManaged
                              ? undefined
                              : () => handleEdit(item._id)
                          }
                        />
                      </Box>
                      <Box>
                        <Text weight="bold">
                          {item.name} {item.isMenu && "(Menu)"}
                        </Text>
                        {item.feeLabel && (
                          <Text>
                            {item.feeLabel}: ${roundPrice(item.fee)}
                          </Text>
                        )}
                        {item.taxPercent && (
                          <Text>Tax Rate: {roundPrice(item.taxPercent)}%</Text>
                        )}
                        {partnerManaged && (
                          <Text size="small" color="dark-5">
                            {t(
                              item.commerceConnection.activationMode === "loxe"
                                ? "settingsUpsell.loxeManagedActivation"
                                : "settingsUpsell.readOnlyPartnerContent",
                            )}
                          </Text>
                        )}
                      </Box>
                    </Box>
                    {!partnerManaged && (
                      <Box
                        direction="row"
                        gap="medium"
                        className="sortable-item-column"
                      >
                        <Button
                          icon={<Edit size="small" />}
                          size="small"
                          label={t("settingsUpsell.editCategory")}
                          alignSelf="center"
                          primary
                          color="status-warning"
                          onClick={() => handleEdit(item._id)}
                        />
                        <Button
                          icon={<Trash size="small" />}
                          size="small"
                          label={t("settingsUpsell.removeCategory")}
                          alignSelf="center"
                          primary
                          color="status-critical"
                          onClick={() => handleRemoveCategory(item._id)}
                        />
                      </Box>
                    )}
                  </Box>
                  <ProductsMenu hotel={hotel} category={item} />
                </AccordionPanel>
              </Accordion>
            </Box>
          ) : (
            <Box
              direction="row"
              gap="medium"
              justify="start"
              background={!item.active ? "inactive" : "transparent"}
            >
              <Box
                alignContent="center"
                justify="center"
                className="sortable-item-column"
              >
                <SortableList.DragHandle />
              </Box>
              <Box
                direction="row"
                gap="medium"
                width="400px"
                className="sortable-item-column"
                align="start"
              >
                <Box height="70px" width="70px">
                  <Image
                    height="70px"
                    width="70px"
                    fit="cover"
                    src={PhotoService.getURL(item.photo) || noPhoto}
                    className="sortable-item-image"
                    onClick={
                      partnerManaged
                        ? undefined
                        : () => handleEditProduct(item._id)
                    }
                  />
                </Box>
                <Box gap="small" width="330px" className="sortable-item-column">
                  <Text weight="bold">{item.name}</Text>
                  <Text truncate color="dark-6" style={{ fontStyle: "italic" }}>
                    {item.description}
                  </Text>
                  {partnerManaged && (
                    <Text size="xsmall" color="dark-5">
                      {providerName(item)} ·{" "}
                      {t("settingsUpsell.partnerManaged")}
                    </Text>
                  )}
                </Box>
              </Box>
              <Box
                direction="row"
                gap="medium"
                width="70px"
                className="sortable-item-column"
                align="center"
              >
                <Text weight="bold">
                  {partnerManaged
                    ? `${item.priceCurrency || hotel.currency} `
                    : "$"}
                  {roundPrice(item.price)}
                </Text>
              </Box>
              {item.isMenu && (
                <Box
                  className="sortable-item-column"
                  width="100px"
                  justify="center"
                >
                  <Text weight="bold">{item.subCategory}</Text>
                </Box>
              )}
              {!partnerManaged && (
                <Box
                  direction="row"
                  gap="medium"
                  className="sortable-item-column"
                >
                  <Button
                    icon={<Edit size="small" />}
                    size="small"
                    label={t("settingsUpsell.editProduct")}
                    alignSelf="center"
                    primary
                    color="status-warning"
                    onClick={() => handleEditProduct(item._id)}
                  />
                  <Button
                    icon={<Trash size="small" />}
                    size="small"
                    label={t("settingsUpsell.removeProduct")}
                    alignSelf="center"
                    primary
                    color="status-critical"
                    onClick={() => handleRemoveProduct(item._id)}
                  />
                </Box>
              )}
            </Box>
          )
        ) : (
          <div></div>
        )}
      </SortableList.Item>
    );
  };

  const onSortEnd = (newSortedList) => {
    setCategories(newSortedList);
    const oldList = categories;
    const listIds = newSortedList.map((x) => {
      return { _id: x._id, isCategory: x.isCategory };
    });
    CategoryService.updateOrder(listIds).then(
      (response) => {
        //onRefresh();
      },
      (error) => {
        setCategories(oldList);
        presentToastErrorContent(error);
      },
    );
  };

  // const handleImport = () => {
  //   presentLoading();
  //   PmsService.importProducts().then(
  //     (response) => {
  //       onRefresh();
  //       dismissLoading();
  //     },
  //     (error) => {
  //       dismissLoading();
  //     }
  //   );
  // }

  const handleHotelToggle = (name) => async (e) => {
    try {
      await HotelService.updateHotel({ [name]: e.target.checked });
      fetchHotel();
      presentToastSuccess("Setting updated");
    } catch (error) {
      presentToastErrorContent(error);
    }
  };

  return (
    <div style={{ width: "100%" }}>
      <Heading level="1">{t("settingsUpsell.title")}</Heading>

      <Box
        background="light-2"
        round="small"
        pad="medium"
        gap="medium"
        margin={{ bottom: "medium" }}
      >
        <Heading level={3} margin="none">
          {t("settingsUpsell.checkinFlowUpsells")}
        </Heading>

        <Box direction="row" gap="small" align="center">
          <CheckBox
            checked={!!hotel.offerLateCheckout}
            onChange={handleHotelToggle("offerLateCheckout")}
            label={t("settingsUpsell.offerLateCheckout")}
            toggle
          />
          <Tip content={t("settingsUpsell.lateCheckoutTip")}>
            <CircleInformation size="small" />
          </Tip>
        </Box>

        <Box direction="row" gap="small" align="center">
          <CheckBox
            checked={!!hotel.offerUpgrade}
            onChange={handleHotelToggle("offerUpgrade")}
            label={t("settingsUpsell.offerRoomUpgrade")}
            toggle
          />
          <Tip content={t("settingsUpsell.roomUpgradeTip")}>
            <CircleInformation size="small" />
          </Tip>
        </Box>
      </Box>

      <Box
        direction="row"
        justify="between"
        align="center"
        gap="medium"
        margin={{ bottom: "small" }}
      >
        <Box>
          <Heading level="2" margin="none">
            {t("settingsUpsell.catalogTitle")}
          </Heading>
          <Text size="small" color="dark-5">
            {t("settingsUpsell.catalogDescription")}
          </Text>
        </Box>
        <Button
          href="/settings/upsell-integrations"
          icon={<Link size="small" />}
          label={t("settingsUpsell.manageIntegrations")}
        />
      </Box>

      <Header
        align="center"
        direction="row"
        justify="start"
        gap="medium"
        fill="horizontal"
      >
        {/*<Button icon={<Download size="small"/>} label="Import from PMS" primary color="status-warning" onClick={handleImport}/>*/}
        <Button
          primary
          icon={<Add size="small" />}
          size="small"
          label={t("settingsUpsell.addCategory")}
          onClick={handleAdd}
        />
        <Button
          primary
          icon={<Add size="small" />}
          size="small"
          label={t("settingsUpsell.addProduct")}
          onClick={handleAddProduct}
        />
        {openEdit && (
          <AddCategory categoryId={categoryId} onClose={handleEditModalClose} />
        )}
        {openEditProduct && (
          <AddProduct
            productId={productId}
            category={{}}
            onClose={handleEditProductModalClose}
            hotel={hotel}
          />
        )}
      </Header>
      <Box width="xxlarge">
        <SortableList
          items={categories.map((x) => {
            return { ...x, id: x._id };
          })}
          onChange={onSortEnd}
          renderItem={(item) => <SortableItem item={item} />}
        />
      </Box>
    </div>
  );
}

SettingsUpsell.propTypes = {};
SettingsUpsell.defaultProps = {};
